
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Button } from './button';
import { MapPin, Move } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLeafletMap } from '@/hooks/use-leaflet-map';

// Define Leaflet types
declare global {
  interface Window {
    L: any;
  }
}

interface MapLocationPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialLatitude?: number;
  initialLongitude?: number;
  onLocationSelect: (latitude: number, longitude: number) => void;
}

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  open,
  onOpenChange,
  initialLatitude,
  initialLongitude,
  onLocationSelect
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [currentPosition, setCurrentPosition] = useState<{lat: number, lng: number}>({
    lat: initialLatitude || 37.7749,
    lng: initialLongitude || -122.4194
  });
  const [isDragging, setIsDragging] = useState(false);
  const { isLoaded } = useLeafletMap();
  
  // Initialize map when dialog opens
  useEffect(() => {
    if (!open || !mapContainerRef.current || !isLoaded || !window.L) return;
    
    console.log("Initializing map with position:", currentPosition);
    
    // Create map instance if it doesn't exist
    if (!mapRef.current) {
      const map = window.L.map(mapContainerRef.current).setView(
        [currentPosition.lat, currentPosition.lng], 
        15
      );
      
      // Add OpenStreetMap tile layer
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      
      mapRef.current = map;
      
      // Add marker
      const marker = window.L.marker([currentPosition.lat, currentPosition.lng], {
        draggable: true,
        title: "Drag me to adjust location"
      }).addTo(map);
      
      markerRef.current = marker;
      
      // Add event listeners to marker
      marker.on("dragstart", () => {
        console.log("Marker drag started");
        setIsDragging(true);
      });
      
      marker.on("drag", (e: any) => {
        console.log("Marker being dragged", e.target.getLatLng());
      });
      
      marker.on("dragend", (e: any) => {
        const position = e.target.getLatLng();
        console.log("Marker drag ended at:", position);
        setCurrentPosition({ lat: position.lat, lng: position.lng });
        setIsDragging(false);
      });
      
      // Add click event to map to move marker
      map.on("click", (e: any) => {
        console.log("Map clicked at:", e.latlng);
        const { lat, lng } = e.latlng;
        marker.setLatLng([lat, lng]);
        setCurrentPosition({ lat, lng });
      });
    } else {
      // If map already exists, just update its view
      mapRef.current.setView([currentPosition.lat, currentPosition.lng], 15);
      if (markerRef.current) {
        markerRef.current.setLatLng([currentPosition.lat, currentPosition.lng]);
      }
    }
    
    // Invalidate size to ensure the map renders correctly in the dialog
    setTimeout(() => {
      if (mapRef.current) {
        console.log("Invalidating map size");
        mapRef.current.invalidateSize();
      }
    }, 250);
  }, [open, isLoaded]);
  
  // Update map when initialLatitude or initialLongitude changes
  useEffect(() => {
    if (initialLatitude && initialLongitude && open && mapRef.current && markerRef.current && isLoaded) {
      console.log("Updating map with new initial position:", { lat: initialLatitude, lng: initialLongitude });
      setCurrentPosition({
        lat: initialLatitude,
        lng: initialLongitude
      });
      
      mapRef.current.setView([initialLatitude, initialLongitude], 15);
      markerRef.current.setLatLng([initialLatitude, initialLongitude]);
      
      // Invalidate size to ensure the map renders correctly
      mapRef.current.invalidateSize();
    }
  }, [initialLatitude, initialLongitude, isLoaded, open]);
  
  const handleConfirmLocation = () => {
    console.log("Confirming location:", currentPosition);
    onLocationSelect(currentPosition.lat, currentPosition.lng);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Adjust Location
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-2">
          <p className="text-sm text-muted-foreground mb-2">
            Click on the map or drag the pin to adjust the location.
          </p>
          
          <div className="relative h-[400px] w-full border rounded-md overflow-hidden">
            <div ref={mapContainerRef} className="h-full w-full" />
            
            {isDragging && (
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm flex items-center">
                <Move className="h-4 w-4 mr-1" /> Dragging pin...
              </div>
            )}
          </div>
          
          <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="flex flex-col sm:flex-row gap-4 text-sm">
              <div>
                <span className="font-medium">Latitude:</span> {currentPosition.lat.toFixed(6)}
              </div>
              <div>
                <span className="font-medium">Longitude:</span> {currentPosition.lng.toFixed(6)}
              </div>
            </div>
            <a 
              href={`https://www.openstreetmap.org/?mlat=${currentPosition.lat}&mlon=${currentPosition.lng}&zoom=15`} 
              target="_blank" 
              rel="noopener noreferrer"
              className={cn(
                "text-xs flex items-center gap-1 text-primary hover:underline"
              )}
            >
              View in OpenStreetMap
            </a>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} type="button">
            Cancel
          </Button>
          <Button onClick={handleConfirmLocation} type="button">
            Confirm Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MapLocationPicker;
